<template>
  <div style="width: 100%; display: flex; flex: 1 1 auto; flex-direction: column" v-if="graphState">
    <v-progress-circular
      v-if="loadingData"
      :width="15"
      :size="350"
      :value="loadingValue"
      color="primary"
      style="align-self: center; margin-bottom: auto; margin-top: auto"
    >
      <span class="headline">{{ $t('Pilot.Graphics.LoadingData') }}</span>
    </v-progress-circular>
    <template v-else-if="plotVariables.length > 0 && chartdata.labels.length > 0">
      <v-toolbar
        height="40"
        ref="toolbar"
        class="pt-2 elevation-0"
        style="z-index: 10; background: none; pointer-events: none"
      >
        <v-menu offset-y>
          <v-btn
            small
            slot="activator"
            style="background: none; pointer-events: all"
            color="primary"
            dark
            :title="$t('Pilot.Graphics.ZoomBehaviourTooltip')"
          >
            {{ tryTranslate(zoomVariable) }}
            <v-icon right dark>mdi-magnify-plus</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile
              v-for="zoom in ['ZoomXAxis', 'ZoomYAxis', 'Both']"
              :key="zoom"
              @click="$store.myActions.UI_SET_GRA_ZOOM({ uiid, zoom })"
            >
              <v-list-tile-title>{{ tryTranslate(zoom) }}</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-menu offset-y>
          <v-btn
            small
            slot="activator"
            style="background: none; pointer-events: all"
            color="primary"
            dark
            :title="$t('Pilot.Graphics.PointSizeTooltip')"
          >
            {{ pointSize }}
            <v-icon right dark>mdi-chart-bubble</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile
              v-for="size in [1, 2, 3, 4, 5]"
              :key="size"
              @click="$store.myActions.UI_SET_GRA_POINT_SIZE({ uiid, size })"
            >
              <v-list-tile-title>{{ size }}</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-menu offset-y max-height="97%">
          <v-btn
            small
            icon
            slot="activator"
            style="background: none; pointer-events: all"
            color="primary"
            dark
            :title="$t('Pilot.Graphics.YAxisVariableTooltip')"
          >
            <v-icon small>mdi-axis-z-arrow</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile
              v-for="(plotVariable, idx) in plotVariables"
              :key="`ord_${plotVariable.field.name}_${idx}`"
              @click="$store.myActions.UI_SET_NMEA_GRA_CONFIG({ config: plotVariable, uiid })"
            >
              <v-list-tile-title>
                <v-icon
                  v-text="
                    graphState.fields.findIndex(
                      f => f.nmea == plotVariable.nmea && f.field.name == plotVariable.field.name,
                    ) >= 0
                      ? 'mdi-checkbox-marked'
                      : 'mdi-checkbox-blank-outline'
                  "
                />
                {{ $i18n.locale == 'fr' ? plotVariable.field.libelleFr : plotVariable.field.libelleEn }}
                <span v-if="plotVariable.field.unit">[{{ plotVariable.field.unit }}]</span>
              </v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-menu offset-y>
          <v-btn
            small
            icon
            slot="activator"
            style="background: none; pointer-events: all"
            color="primary"
            dark
            :title="$t('Pilot.Graphics.LastDataModeTooltip')"
          >
            <v-icon small>mdi-av-timer</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile
              v-for="dlm in dataLimitModes"
              :key="dlm"
              :class="{ selected: dlm == graphState.dataLimit }"
              @click="$store.myActions.UI_SET_GRA_DATA({ uiid, name: 'dataLimit', value: dlm })"
            >
              <v-list-tile-title>{{ $t(`Pilot.Graphics.LimitData${dlm}`) }}</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-spacer />
        <div class="d-flex flex-grow-1 align-self-start" style="position: relative">
          <v-spacer />
          <v-btn
            class="flex-grow-0"
            style="pointer-events: all"
            small
            icon
            @click.stop.prevent="resetZoom"
            :title="$t('Pilot.Graphics.ResetGraphTooltip')"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </v-toolbar>
      <div ref="container" style="flex: 1 0 auto; position: relative; margin-top: -10px">
        <line-chart
          ref="lineChart"
          @onPointClick="onPointClick"
          v-if="graphState.fields.every(f => !nmeaLoading[f.nmea]) && !hideGraph"
          :chart-data="chartdata"
          :options="{
            animation: { duration: 0 },
            legend: { display: true, labels: { fontColor: '#000' } },
            responsive: true,
            maintainAspectRatio: false,
            exportEnabled: true,
            plugins: {
              zoom: {
                pan: {
                  enabled: true,
                  mode: 'xy',
                },
                zoom: {
                  enabled: true,
                  mode: graphState.zoom == 'ZoomXAxis' ? 'x' : graphState.zoom == 'ZoomYAxis' ? 'y' : 'xy',
                },
              },
            },
            scales: {
              xAxes: [
                {
                  type: 'time',
                  scaleLabel: { display: false, labelString: 'Date', padding: 0 },
                },
              ],
            },
          }"
          :width="300"
          :styles="{ height: '100%' }"
        ></line-chart>
        <div class="text-xs-center" v-else>
          <v-progress-circular :size="70" :width="7" indeterminate></v-progress-circular>
        </div>
      </div>
    </template>
    <template v-else>
      <span class="display-2" style="margin: auto">{{ $t('Pilot.Nmea.NoDataAvailable') }}</span>
    </template>
  </div>
</template>

<script lang="ts">
import { DataPoolGlobalKeys } from '@/enum/DataPool'
import { NmeaConfig } from '@/models/datapool/NmeaConfig'
import { NmeaConfigField } from '@/models/datapool/NmeaConfigField'
import { NmeasConfig } from '@/models/datapool/NmeasConfig'
import LineChart from '@/components/charts/LineChart'
import Vue from 'vue'
//@ts-ignore
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { mustIgnoreValue } from '@/utils/autoscale'
import { scale } from 'ol/size'
import { head } from 'lodash'
import { ticks } from 'd3-array'
import { buildMissionUIStateId } from '@/models/ui/MissionUiState'
import moment, { duration, Moment } from 'moment'
import { GraphNmeaState } from '@/models/ui/GraphNmeaState'
import 'chartjs-plugin-zoom'
import { chartColor } from '@/utils/autoscale'
import { dataLimitMode } from '@/models/ui/GraphUiState'

@Component({
  name: 'MissionDetailNmeaGraphics',
  components: { LineChart },
  props: {
    isleft: Boolean,
    uiid: {
      type: String,
      required: true,
    },
  },
})
export default class MissionDetailNmeaGraphics extends Vue {
  isleft!: boolean
  uiid!: string

  get selectedUi() {
    return this.isleft
      ? this.$store.myGetters.currentDetailMission.tabsleft.selected
      : this.$store.myGetters.currentDetailMission.tabsright.selected
  }

  hideGraph = false
  interval = undefined as undefined | number
  loadingData = true
  loadingValue = 0
  nmeasConfig = new NmeasConfig()
  nmeaLoading = {} as { [name: string]: boolean }

  dataLimitModes = [dataLimitMode.LAST_12H, dataLimitMode.LAST_24H, dataLimitMode.ALL]

  get originalNmeaConfigStr() {
    return (this.$store.myGetters.dataPoolItemsValueByKey(DataPoolGlobalKeys['global.config.nmea']) || '') as string
  }

  get mission() {
    return this.$store.myGetters.currentDetailMission.mission
  }

  get nmeas() {
    return [
      ...this.nmeasConfig.rowInit.filter(
        r => this.mission.nmeaTypeArray.findIndex(n => r.name.toLowerCase() == n.toLowerCase()) > -1,
      ),
      ...this.nmeasConfig.rowLazy.filter(
        r => this.mission.nmeaTypeArray.findIndex(n => r.name.toLowerCase() == n.toLowerCase()) > -1,
      ),
    ]
  }

  get plotVariables() {
    return this.nmeas.reduce((graphes, nmea) => {
      nmea.col1?.filter(d => d.canBePlot)?.forEach(d => graphes.push({ nmea: nmea.name, field: d }))
      nmea.col2?.filter(d => d.canBePlot)?.forEach(d => graphes.push({ nmea: nmea.name, field: d }))
      nmea.col3?.filter(d => d.canBePlot)?.forEach(d => graphes.push({ nmea: nmea.name, field: d }))
      return graphes
    }, [] as Array<{ nmea: string; field: NmeaConfigField }>)
  }

  get minYos() {
    return Math.min(...this.mission.yoArray.map(y => y.yosCycle!))
  }

  get maxYos() {
    return Math.max(...this.mission.yoArray.map(y => y.yosCycle!))
  }

  get yoIndex() {
    return this.missionState && this.missionState.timelineIndex != -1
      ? this.missionState.timelineIndex
      : this.mission.yoArray.length - 1
  }

  get missionUiid() {
    return buildMissionUIStateId(this.mission.misId!, 'detail')
  }
  get missionState() {
    return this.$store.myGetters.missionUiStateByUiid(this.missionUiid)
  }

  get graphState() {
    return this.$store.myGetters.graphNmeaStateByUiid(this.uiid)
  }

  get fields() {
    return this.graphState?.fields
  }

  get curYoIndex() {
    return this.missionState && this.missionState.timelineIndex != -1
      ? this.missionState.timelineIndex
      : this.mission.yoArray.length - 1
  }

  get pointSize() {
    return this.graphState?.pointSize
  }

  get graphToYoIndex() {
    return this.mission.yoArray
      .map((yo, index) => ({ yo, index }))
      .filter(y => !y.yo.isUnderWater && this.isInDateLimiteRange(y.yo.yoGpsDatTime))
      .map(y => y.index)
  }

  get zoomVariable() {
    return this.graphState?.zoom
  }

  created() {
    this.loadingData = true
    this.$store.myActions.DATAPOOL_ENSURE({ fullKey: DataPoolGlobalKeys['global.config.nmea'] }).then(() => {
      this.nmeasConfig = NmeasConfig.createNmeasConfig(this.originalNmeaConfigStr)
      this.loadingData = false
      clearInterval(this.interval)
    })
  }

  mounted() {
    if (this.loadingData) {
      this.interval = setInterval(() => {
        this.loadingValue = (this.loadingValue + 10) % 110
      }, 1000)
    }
  }

  @Watch('zoomVariable')
  onZoomVariableChanged() {
    this.resetGraph()
  }

  @Watch('pointSize')
  onPointSizeChanged() {
    this.updateChart()
  }

  @Watch('originalNmeaConfigStr')
  onOriginalNmeaConfigStrChange() {
    this.nmeasConfig = NmeasConfig.createNmeasConfig(this.originalNmeaConfigStr)
  }

  @Watch('nmeas')
  onNmeasChanged(newValues: Array<NmeaConfig>) {
    newValues.forEach(c => {
      if (c.name != 'computed') {
        this.$set(this.nmeaLoading, c.name, true)
        this.$store.myActions.MISSION_ENSURE_DATA_NMEA({ typeNmea: c.name, id: this.mission.misId! }).finally(() => {
          this.$set(this.nmeaLoading, c.name, false)
          //this.nmeaLoading[c.name] = false
        })
      }
    })
  }

  @Watch('fields')
  onFieldsChange(newFields: Array<{ nmea: string; field: NmeaConfigField }>) {
    if (newFields.length > 0) {
      const field = newFields[0].field
      const title = `${this.$i18n.locale == 'fr' ? field.libelleFr : field.libelleEn}${
        field.unit.length > 0 ? ` [${field.unit}]` : ''
      }`
      const uiidParts = this.uiid.split('-')
      if (uiidParts.length == 2)
        this.$store.myActions.MISSION_RENAME_DETAIL({
          cmp: uiidParts[0],
          id: Number.parseInt(uiidParts[1]),
          isleft: this.isleft,
          label: title,
        })
    }
  }

  @Watch('curYoIndex')
  onCurYoIndexChange() {
    this.updateChart()
  }

  updateChart() {
    //@ts-ignore
    if (this.$refs.lineChart && this.$refs.lineChart.chart) this.$refs.lineChart.chart.update()
    else {
      this.resetGraph()
    }
  }

  @Watch('selectedUi')
  onSelectedUiChange() {
    this.updateChart()
  }

  stopClick() {}

  onPointClick(index: number) {
    //@ts-ignore
    this.$store.myActions.UI_SELECT_TIMELINE_MISSION_INDEX({
      index: this.graphToYoIndex[index],
      uiid: this.missionUiid,
    })
  }

  getNextYo(val: number, decalage: number = 1) {
    let yo = decalage == 0 ? this.mission.yoArray[val] : null
    let offset = 1
    //on prend la valeur absolue du décalage
    decalage = Math.abs(decalage)
    while (decalage > 0) {
      while (this.mission.yoArray.length > val + offset && (yo = this.mission.yoArray[val + offset]).isUnderWater) {
        offset++
      }
      decalage--
    }
    if (yo && !yo.isUnderWater) return yo.yosCycle
  }

  resetZoom() {
    //@ts-ignore
    if (this.$refs.lineChart && this.$refs.lineChart.chart) {
      //@ts-ignore
      this.$refs.lineChart?.chart?.resetZoom()
    } else {
      this.resetGraph()
    }
  }

  tryTranslate(key: string) {
    const transKey = `Pilot.Graphics.${key}`
    const trad = this.$t(transKey)
    return transKey == trad ? key : trad
  }

  resetGraph() {
    this.hideGraph = true
    this.$nextTick(() => (this.hideGraph = false))
  }

  isInDateLimiteRange(date: Date) {
    if (this.graphState?.dataLimit == dataLimitMode.ALL) return true
    const offsetH = 3600000 * (this.graphState?.dataLimit == dataLimitMode.LAST_12H ? 12 : 24)
    //@ts-ignore
    const maxDateTime = this.mission.yoArray.at(-1)?.yoGpsDatTime.getTime()!
    return maxDateTime - date.getTime() <= offsetH
  }

  get chartdata() {
    const graphDatas = {
      labels: this.mission.yoArray
        .filter(
          yo =>
            !yo.isUnderWater &&
            yo.yoGpsDatTime.toString() != 'Invalid Date' &&
            this.isInDateLimiteRange(yo.yoGpsDatTime),
        )
        .map(yo => this.parseDateToUtc(yo.yoGpsDatTime)),
      dataDic: this.graphState?.fields?.reduce((dic, field) => {
        const dataNmea = this.mission.dataNmea[field.nmea!.toLowerCase()]
        if (!dataNmea?.dataSet) return dic
        const header = dataNmea?.columns?.find(h => h?.dbName?.toLowerCase() == field.field.name?.toLowerCase())
        if (header?.dbName?.endsWith('_LAT') || header?.dbName?.endsWith('_LON') || header?.dbTypeName == 'datetime2')
          return dic
        dic[`${field.nmea}_${field.field.name}`] = this.mission.yoArray
          .map((yo, index) => {
            const yoForData = this.getNextYo(index, field.field.offsetYoForPlot ?? 0)
            return {
              data: yo.isUnderWater
                ? null
                : yoForData == null ||
                  yoForData == undefined ||
                  dataNmea?.dataSet[yoForData] == undefined ||
                  mustIgnoreValue(dataNmea?.dataSet[yoForData][header?.pos!])
                ? null
                : dataNmea?.dataSet[yoForData!][header?.pos!] ?? null,
              yo,
            }
          })
          ?.filter(d => !d.yo.isUnderWater && this.isInDateLimiteRange(d.yo.yoGpsDatTime))
          ?.map(d => d.data)
        return dic
      }, {} as { [index: string]: any }),
    }

    return {
      labels: graphDatas?.labels,
      datasets: this.graphState?.fields
        ?.filter(field => (graphDatas.dataDic[`${field.nmea}_${field.field.name}`]?.length ?? 0) > 0)
        .map((field, index) => ({
          label: `${this.$i18n.locale == 'fr' ? field.field.libelleFr : field.field.libelleEn} ${
            field.field.unit ? `[${field.field.unit}]` : ''
          }`,
          backgroundColor: '#FFFFFF00',
          borderColor: chartColor[index],
          lineTension: 0,
          borderWidth: 1,
          pointRadius: (ctx: any) => {
            return this.graphToYoIndex[ctx.dataIndex] == this.curYoIndex ? this.pointSize + 1 : this.pointSize
          },
          pointBorderColor: (ctx: any) => {
            return this.graphToYoIndex[ctx.dataIndex] == this.curYoIndex ? '#22b722' : chartColor[index]
          },
          pointBackgroundColor: (ctx: any) => {
            return this.graphToYoIndex[ctx.dataIndex] == this.curYoIndex ? '#22b722' : chartColor[index]
          },
          data: graphDatas.dataDic[`${field.nmea}_${field.field.name}`],
          spanGaps: true,
        })),
    }
  }

  @Watch('chartdata')
  onChatDataChanged() {
    console.log(this.chartdata)
    this.resetGraph()
  }

  parseDateToUtc(data: any) {
    return moment(data).utc().format('YYYY-MM-DD HH:mm') //this.$options.filters['formatDate'](data,"YYYY-MM-DD HH:mm")
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
.graphnmea {
  border: 1px solid #ddd;
  box-sizing: content-box;
  width: 100%;
  height: 200px;
  position: relative;
  cursor: pointer;
}
</style>

<style scoped>
.selected {
  background-color: var(--secondary-color);
  color: white;
}
</style>
